/**
 * Nav access control driven by environment variables.
 *
 * Each key maps to a REACT_APP_NAV_ACCESS_* env var that holds a
 * comma-separated list of loginAs role IDs that may see that menu item.
 *
 * Rules:
 *   • If the env var is set   → only the listed IDs can see the item.
 *   • If the env var is empty / unset → the item is visible to everyone.
 *
 * To override access at runtime inject the variable via `docker run -e`
 * or `--env-file`; the docker-entrypoint.sh writes them to window._env_.
 *
 * Example (restrict docs locker to admins only):
 *   REACT_APP_NAV_ACCESS_DOCS_LOCKER=1,2
 */

import { getEnvNumberList } from "./env";

/**
 * Returns true when the given loginAs ID is allowed to access the feature.
 * An empty `ids` array means "no restriction — open to all".
 *
 * @param {number[]} ids - Allowed role IDs (empty = unrestricted)
 * @param {number|undefined} loginAsId
 */
export const canNavAccess = (ids, loginAsId) => {
  if (!ids || ids.length === 0) return true;
  return ids.includes(Number(loginAsId));
};

/**
 * Resolved access lists per nav item.
 * Fallbacks reflect the previous hardcoded role IDs so existing deployments
 * keep working without any env changes.
 */
export const NAV_ACCESS = {
  /** Dashboard — open to all by default */
  dashboard: getEnvNumberList("REACT_APP_NAV_ACCESS_DASHBOARD"),

  /** Notifications — open to all by default */
  notifications: getEnvNumberList("REACT_APP_NAV_ACCESS_NOTIFICATIONS"),

  /** My agreements list — open to all by default */
  myAgreements: getEnvNumberList("REACT_APP_NAV_ACCESS_MY_AGREEMENTS"),

  /** New agreement (individual, bulk, legacy) */
  newAgreement: getEnvNumberList("REACT_APP_NAV_ACCESS_NEW_AGREEMENT", [1, 2, 3, 4]),

  /** Docs locker */
  docsLocker: getEnvNumberList("REACT_APP_NAV_ACCESS_DOCS_LOCKER", [1, 2, 3, 4, 7]),

  /** Document chat */
  documentChat: getEnvNumberList("REACT_APP_NAV_ACCESS_DOCUMENT_CHAT", [1, 2, 3, 4, 7]),

  /** Templates (agreements + clauses) */
  templates: getEnvNumberList("REACT_APP_NAV_ACCESS_TEMPLATES", [1, 2, 4]),

  /** Obligations — open to all by default */
  obligations: getEnvNumberList("REACT_APP_NAV_ACCESS_OBLIGATIONS"),
};
